import {IVideoPeakDTO} from '../Models/DTOs/IVideoPeakDTO';
import axios, {AxiosError} from "axios";

// Constants
import config from "../Constants/Config";

// Models
import CountWrapper from "../Models/DTOs/CountWrapper";
import VideoDTO, {ContinueWatchingDTO} from "../Models/DTOs/VideoDTO";
import ILatestContentDTO from "../Models/DTOs/ILatestContentDTO";
import {IRelatedVideoInfo} from "../Models/DTOs/IRelatedVideoInfo";

const LEGACY_URL = process.env.REACT_APP_API_URL + '/video-single/';
const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;

export async function GetLatestVideo(
    seriesId: number,
    abortController: AbortController
): Promise<VideoDTO | null> {
    try {
        const data = await axios
            .get(
                `${WEB_API_URL}v2${config.videos}?seriesId=${seriesId}&take=1&order=desc&publishedOnly=true`,
                { signal: abortController.signal }
            )
            .then((result) => result.data as CountWrapper<VideoDTO>);

        const latest = data.items?.[0] ?? null;
        return latest;
    } catch (error) {
        const err = error as AxiosError;

        // don’t treat intentional cancels like "real" errors
        if ((err as any)?.name === "CanceledError") throw err;

        console.log("GetLatestVideo error", err);
        throw err;
    }
}

export async function GetVideo(
    id: number,
    abortController: AbortController,
    accessToken?: string | null
): Promise<VideoDTO> {
    try {
        const video = await axios
            .get(`${WEB_API_URL}v2/videos/${id}`, {
                signal: abortController.signal,
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
            })
            .then((response) => response.data as VideoDTO);

        return video;
    } catch (error) {
        const err = error as AxiosError;

        // Don’t log intentional cancels like real errors
        if ((err as any)?.name === "CanceledError") throw err;

        console.log("Get video-single error:", err);
        throw err;
    }
}

export interface VideoPeakResponse {
    peak: number;
    last_updated: string | null;
}

export async function GetVideoPeak(
    userId: string,
    videoId: number,
    abortController: AbortController
): Promise<VideoPeakResponse> {
    const response = await axios.get(
        `${WEB_API_URL}v1/users/${encodeURIComponent(userId)}/videos/${videoId}/peak`,
        { signal: abortController.signal, timeout: 10000 }
    );
    const data = response.data;
    if (!data || typeof data.peak !== "number" || !Number.isSafeInteger(data.peak) || data.peak < 0) {
        throw new Error("Invalid video progress response.");
    }
    return { peak: data.peak, last_updated: data.last_updated ?? null };
}

export async function UpdateVideoPeak(userId: string, videoId: number, peak: number): Promise<boolean> {
    if (!userId || !Number.isSafeInteger(videoId) || videoId <= 0 || !Number.isSafeInteger(Math.floor(peak)) || peak < 0) {
        return false;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
        const response = await fetch(
            `${WEB_API_URL}v1/users/${encodeURIComponent(userId)}/videos/${videoId}/peak`,
            {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ peak: Math.floor(peak) }),
                keepalive: true,
                signal: controller.signal,
            }
        );
        return response.ok;
    } catch {
        return false;
    } finally {
        clearTimeout(timeout);
    }
}

export async function GetLatestFour(abortController: AbortController) {
    const numberToGet = 4;
    var response = await axios
        .get(LEGACY_URL + "GetLatestContent/" + numberToGet, {signal: abortController.signal})
        .then((response) => {
            return response.data as ILatestContentDTO[];
        })
        .catch((error: AxiosError) => {
            console.log("GetLatestFour error ");
            return error;
        });

    return response;
}

export async function GetContinueWatching(
    aspNetUserId: string,
    accessToken: string, // <-- Add this parameter
    abortController: AbortController,
    take: number = 15,
    skip: number = 0
): Promise<ContinueWatchingDTO | AxiosError> {
    const url = `${WEB_API_URL}v2/users/${encodeURIComponent(aspNetUserId)}/videos/continue-watching`;

    try {
        const res = await axios.get(url, {
            signal: abortController.signal,
            params: { take, skip },
            headers: { Authorization: `Bearer ${accessToken}` } // <-- Add the Auth header
        });
        return res.data as ContinueWatchingDTO;
    } catch (err) {
        const error = err as AxiosError;
        if ((error as any)?.name === "CanceledError") throw error;
        console.error("GetContinueWatching error", error);
        return error;
    }
}

export async function GetRelatedVideosInfo(videoId: number, countryCode: string, abortController: AbortController) {
    var response = await axios
        .get(LEGACY_URL + "GetRelatedLinks/" + videoId + "/" + countryCode + "/web", {signal: abortController.signal})
        .then((response) => {
            return response.data as IRelatedVideoInfo;
        })
        .catch((error: AxiosError) => {
            console.log(error);
            return null;
        });

    return response;
}

export async function GetVideosBySeason(
    seriesId: number,
    seasonId: number,
    take: number,
    skip: number,
    order: string,
    abortController: AbortController,
): Promise<CountWrapper<VideoDTO>> {
    try {
        const params: Record<string, any> = {
            seriesId,
            seasonId,
            take,
            skip,
            order
        };

        return await axios
            .get<CountWrapper<VideoDTO>>(`${WEB_API_URL}v2${config.videos}`, {
                signal: abortController.signal,
                params,
            })
            .then((r) => r.data);
    } catch (error) {
        console.log("GetVideosBySeriesSeasonPage");
        throw error as AxiosError;
    }
}

export interface GetVideosBySeriesArgs {
    seriesId: number | string;
    take?: number;
    skip?: number;
    order?: string;
    publishedOnly?: boolean;
    signal?: AbortSignal;
}

export async function GetVideosBySeries(
    args: GetVideosBySeriesArgs
): Promise<CountWrapper<VideoDTO>> {
    const {
        seriesId,
        take = 20,
        skip = 0,
        order = "desc",
        publishedOnly = true,
        signal,
    } = args;

    return axios
        .get(`${WEB_API_URL}v2${config.videos}`, {
            params: { seriesId, take, skip, order, publishedOnly },
            signal, // ✅
        })
        .then((result) => {
            // console.log("GetVideosBySeries response:", result.data);
            return result.data as CountWrapper<VideoDTO>;
        })
        .catch((error: AxiosError) => {
            // Optional: don’t spam logs for intentional aborts
            if ((error as any)?.name === "CanceledError") throw error;

            console.log("GetVideosBySeries error", error);
            throw error;
        });
}

export interface GetLatestVideosArgs {
    take?: number;
    publishedOnly?: boolean;
    signal?: AbortSignal;
}

export async function GetLatestVideos(
    args: GetLatestVideosArgs = {}
): Promise<CountWrapper<VideoDTO>> {
    const {
        take = 30,
        publishedOnly = true,
        signal,
    } = args;

    return axios
        .get(`${WEB_API_URL}v2${config.videos}`, {
            params: { take, publishedOnly },
            signal, // ✅
        })
        .then((result) => result.data as CountWrapper<VideoDTO>)
        .catch((error: AxiosError) => {
            // Don’t log intentional cancels
            if ((error as any)?.name === "CanceledError") throw error;

            console.log("GetLatestVideos error", error);
            throw error;
        });
}

export interface GetFilmsArgs {
    take?: number;
    skip?: number;
    publishedOnly?: boolean;
    filmsOnly?: boolean;
    signal?: AbortSignal;
}

export async function GetFilms(args: GetFilmsArgs = {}): Promise<CountWrapper<VideoDTO>> {
    const {
        take = 30,
        skip = 0,
        publishedOnly = true,
        filmsOnly = true,
        signal,
    } = args;

    return axios
        .get(`${WEB_API_URL}v2${config.videos}`, {
            params: { take, skip, publishedOnly, filmsOnly },
            signal,
        })
        .then((result) => result.data as CountWrapper<VideoDTO>)
        .catch((error: AxiosError) => {
            if ((error as any)?.name === "CanceledError") throw error;
            console.log("GetFilms error", error);
            throw error;
        });
}

export interface GetTopRatedVideosArgs {
    take?: number;
    signal?: AbortSignal;
}

export async function GetTopRatedVideos(
    args: GetTopRatedVideosArgs = {}
): Promise<CountWrapper<VideoDTO>> {
    const {
        take = 10,
        signal,
    } = args;

    return axios
        .get(`${WEB_API_URL}v2${config.videos}/top-rated`, {
            params: { take },
            signal, // ✅
        })
        .then((result) => result.data as CountWrapper<VideoDTO>)
        .catch((error: AxiosError) => {
            if ((error as any)?.name === "CanceledError") throw error;

            console.log("GetTopRatedVideos error", error);
            throw error;
        });
}
